import { FormEvent, useState } from "react";
import { LoaderCircle } from "lucide-react";

import {
  createDurationRule,
  createTravelRule,
  updateDurationRule,
  updateTravelRule,
} from "./availability-api";
import type { Region, ServiceDurationRule, ServiceType, TravelBufferRule } from "./types";

type FormShellProps = {
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
};

export function DurationRuleForm({
  record,
  services,
  canWrite,
  onClose,
  onSaved,
}: FormShellProps & { record?: ServiceDurationRule; services: ServiceType[] }) {
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setFailed(false);
    const fields = new FormData(event.currentTarget);
    const input = {
      serviceTypeId: String(fields.get("serviceTypeId")),
      name: String(fields.get("name")),
      active: fields.get("active") === "on",
      priority: integer(fields.get("priority"), 0),
      minimumBedrooms: optionalNumber(fields.get("minimumBedrooms")),
      maximumBedrooms: optionalNumber(fields.get("maximumBedrooms")),
      minimumBathrooms: optionalNumber(fields.get("minimumBathrooms")),
      maximumBathrooms: optionalNumber(fields.get("maximumBathrooms")),
      minimumSquareFeet: optionalNumber(fields.get("minimumSquareFeet")),
      maximumSquareFeet: optionalNumber(fields.get("maximumSquareFeet")),
      crewSize: optionalNumber(fields.get("crewSize")),
      baseDurationMinutes: integer(fields.get("baseDurationMinutes")),
      additionalMinutesPerBedroom: integer(fields.get("additionalMinutesPerBedroom"), 0),
      additionalMinutesPerBathroom: integer(fields.get("additionalMinutesPerBathroom"), 0),
      squareFeetBlockSize: optionalNumber(fields.get("squareFeetBlockSize")),
      additionalMinutesPerSquareFeetBlock: integer(
        fields.get("additionalMinutesPerSquareFeetBlock"),
        0,
      ),
      effectiveFrom: String(fields.get("effectiveFrom")),
      effectiveUntil: nullable(fields.get("effectiveUntil")),
      notes: nullable(fields.get("notes")),
    };
    try {
      await (record ? updateDurationRule(record.id, input) : createDurationRule(input));
      await onSaved();
    } catch {
      setFailed(true);
      setSending(false);
    }
  }

  return (
    <form className="record-form availability-form" onSubmit={submit}>
      <div className="form-grid">
        <label>
          Service
          <select name="serviceTypeId" defaultValue={record?.serviceTypeId ?? ""} required>
            <option value="" disabled>
              Select a service
            </option>
            {services.map((service) => (
              <option key={service.id} value={service.id}>
                {service.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Rule name
          <input name="name" required maxLength={120} defaultValue={record?.name} />
        </label>
        <NumberField
          name="baseDurationMinutes"
          label="Base minutes"
          value={record?.baseDurationMinutes}
          required
          min={1}
          max={10080}
        />
        <NumberField name="crewSize" label="Crew size" value={record?.crewSize} min={1} max={100} />
        <NumberField
          name="minimumBedrooms"
          label="Minimum bedrooms"
          value={record?.minimumBedrooms}
          min={0}
          max={100}
        />
        <NumberField
          name="maximumBedrooms"
          label="Maximum bedrooms"
          value={record?.maximumBedrooms}
          min={0}
          max={100}
        />
        <NumberField
          name="minimumBathrooms"
          label="Minimum bathrooms"
          value={record?.minimumBathrooms}
          min={0}
          max={100}
          step="0.5"
        />
        <NumberField
          name="maximumBathrooms"
          label="Maximum bathrooms"
          value={record?.maximumBathrooms}
          min={0}
          max={100}
          step="0.5"
        />
        <NumberField
          name="minimumSquareFeet"
          label="Minimum square feet"
          value={record?.minimumSquareFeet}
          min={0}
          max={1000000}
        />
        <NumberField
          name="maximumSquareFeet"
          label="Maximum square feet"
          value={record?.maximumSquareFeet}
          min={0}
          max={1000000}
        />
        <NumberField
          name="additionalMinutesPerBedroom"
          label="Minutes per bedroom"
          value={record?.additionalMinutesPerBedroom ?? 0}
          min={0}
          max={1440}
        />
        <NumberField
          name="additionalMinutesPerBathroom"
          label="Minutes per bathroom"
          value={record?.additionalMinutesPerBathroom ?? 0}
          min={0}
          max={1440}
        />
        <NumberField
          name="squareFeetBlockSize"
          label="Square-foot block size"
          value={record?.squareFeetBlockSize}
          min={1}
          max={1000000}
        />
        <NumberField
          name="additionalMinutesPerSquareFeetBlock"
          label="Minutes per size block"
          value={record?.additionalMinutesPerSquareFeetBlock ?? 0}
          min={0}
          max={1440}
        />
        <NumberField
          name="priority"
          label="Priority"
          value={record?.priority ?? 0}
          min={-10000}
          max={10000}
        />
        <label>
          Effective from
          <input name="effectiveFrom" type="date" required defaultValue={record?.effectiveFrom} />
        </label>
        <label>
          Effective until
          <input name="effectiveUntil" type="date" defaultValue={record?.effectiveUntil ?? ""} />
        </label>
        <label className="toggle-field workforce-toggle">
          <input name="active" type="checkbox" defaultChecked={record?.active ?? true} /> Active
          rule
        </label>
        <label className="wide-field">
          Private notes
          <textarea name="notes" rows={4} maxLength={10000} defaultValue={record?.notes ?? ""} />
        </label>
      </div>
      <p className="form-guidance">
        This stores a configurable rule only. It does not calculate or confirm an appointment yet.
      </p>
      <FormActions failed={failed} sending={sending} canWrite={canWrite} onClose={onClose} />
    </form>
  );
}

export function TravelRuleForm({
  record,
  regions,
  canWrite,
  onClose,
  onSaved,
}: FormShellProps & { record?: TravelBufferRule; regions: Region[] }) {
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSending(true);
    setFailed(false);
    const fields = new FormData(event.currentTarget);
    const input = {
      name: String(fields.get("name")),
      fromRegionId: nullable(fields.get("fromRegionId")),
      toRegionId: nullable(fields.get("toRegionId")),
      bufferMinutes: integer(fields.get("bufferMinutes")),
      priority: integer(fields.get("priority"), 0),
      effectiveFrom: String(fields.get("effectiveFrom")),
      effectiveUntil: nullable(fields.get("effectiveUntil")),
      active: fields.get("active") === "on",
      notes: nullable(fields.get("notes")),
    };
    try {
      await (record ? updateTravelRule(record.id, input) : createTravelRule(input));
      await onSaved();
    } catch {
      setFailed(true);
      setSending(false);
    }
  }

  return (
    <form className="record-form availability-form" onSubmit={submit}>
      <div className="form-grid">
        <label className="wide-field">
          Rule name
          <input name="name" required maxLength={120} defaultValue={record?.name} />
        </label>
        <RegionSelect
          name="fromRegionId"
          label="From region"
          regions={regions}
          value={record?.fromRegionId}
        />
        <RegionSelect
          name="toRegionId"
          label="To region"
          regions={regions}
          value={record?.toRegionId}
        />
        <NumberField
          name="bufferMinutes"
          label="Buffer minutes"
          value={record?.bufferMinutes}
          required
          min={0}
          max={1440}
        />
        <NumberField
          name="priority"
          label="Priority"
          value={record?.priority ?? 0}
          min={-10000}
          max={10000}
        />
        <label>
          Effective from
          <input name="effectiveFrom" type="date" required defaultValue={record?.effectiveFrom} />
        </label>
        <label>
          Effective until
          <input name="effectiveUntil" type="date" defaultValue={record?.effectiveUntil ?? ""} />
        </label>
        <label className="toggle-field workforce-toggle">
          <input name="active" type="checkbox" defaultChecked={record?.active ?? true} /> Active
          rule
        </label>
        <label className="wide-field">
          Private notes
          <textarea name="notes" rows={4} maxLength={10000} defaultValue={record?.notes ?? ""} />
        </label>
      </div>
      <p className="form-guidance">
        Leave either region blank to create a broader fallback. Higher priority wins later when the
        conflict engine evaluates matching rules.
      </p>
      <FormActions failed={failed} sending={sending} canWrite={canWrite} onClose={onClose} />
    </form>
  );
}

function RegionSelect({
  name,
  label,
  regions,
  value,
}: {
  name: string;
  label: string;
  regions: Region[];
  value?: string | null;
}) {
  return (
    <label>
      {label}
      <select name={name} defaultValue={value ?? ""}>
        <option value="">Any region</option>
        {regions.map((region) => (
          <option key={region.id} value={region.id}>
            {region.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function NumberField({
  name,
  label,
  value,
  required = false,
  min,
  max,
  step = "1",
}: {
  name: string;
  label: string;
  value?: number | null;
  required?: boolean;
  min: number;
  max: number;
  step?: string;
}) {
  return (
    <label>
      {label}
      <input
        name={name}
        type="number"
        defaultValue={value ?? ""}
        required={required}
        min={min}
        max={max}
        step={step}
      />
    </label>
  );
}

function FormActions({
  failed,
  sending,
  canWrite,
  onClose,
}: {
  failed: boolean;
  sending: boolean;
  canWrite: boolean;
  onClose: () => void;
}) {
  return (
    <>
      {failed ? (
        <p className="form-error" role="alert">
          We couldn’t save this rule. Check ranges, dates, and duplicate names.
        </p>
      ) : null}
      <div className="form-actions">
        <button type="button" className="secondary-button" onClick={onClose}>
          Cancel
        </button>
        <button className="primary-button compact-action" disabled={sending || !canWrite}>
          {sending ? <LoaderCircle className="spin" /> : null} Save rule
        </button>
      </div>
    </>
  );
}

function integer(value: FormDataEntryValue | null, fallback = Number.NaN) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
}
function optionalNumber(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text === "" ? null : Number(text);
}
function nullable(value: FormDataEntryValue | null) {
  const text = String(value ?? "").trim();
  return text || null;
}
